// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: 10-security-compliance.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
// @greenlight-spec: 48-user-passthrough.md#the-core
import * as React from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DOCS_LINKS } from '@/lib/docsLinks';
import { useIsMobile } from '@/lib/useIsMobile';
import type { PermissionListItem } from '@greenlight/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DecisionVerb = 'approve' | 'deny' | 'revoke';

export interface DecisionDialogProps {
  /** The verb being confirmed, or null when closed. */
  verb: DecisionVerb | null;
  /** The grant being acted on, or null when closed. */
  item: PermissionListItem | null;
  open: boolean;
  /** True while the mutation is in flight (disables confirm, shows pending label). */
  isPending: boolean;
  /** Called with the optional reason (undefined when blank) on confirm. */
  onConfirm: (reason: string | undefined) => void;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Derived label helpers
// ---------------------------------------------------------------------------

function titleForVerb(verb: DecisionVerb): string {
  switch (verb) {
    case 'approve':
      return 'Approve permission';
    case 'deny':
      return 'Deny permission';
    case 'revoke':
      return 'Revoke permission';
  }
}

function confirmLabelForVerb(verb: DecisionVerb, isPending: boolean): string {
  if (!isPending) {
    switch (verb) {
      case 'approve':
        return 'Approve';
      case 'deny':
        return 'Deny';
      case 'revoke':
        return 'Revoke';
    }
  }
  switch (verb) {
    case 'approve':
      return 'Approving…';
    case 'deny':
      return 'Denying…';
    case 'revoke':
      return 'Revoking…';
  }
}

function confirmVariantForVerb(verb: DecisionVerb): 'default' | 'destructive' {
  // At the confirm step both negative actions commit as filled destructive (the modal
  // IS the intent); approve stays the primary blue.
  return verb === 'approve' ? 'default' : 'destructive';
}

/** Who approving points an agent at: the app (ADR-0029 app principal) or the person. */
function proxiedApprovalHolder(item: PermissionListItem): string {
  return item.principal_type === 'app'
    ? `${item.app_name ?? 'this app'} and the coding agents that build it`
    : `${item.requested_by}'s coding agent`;
}

function ProxiedApprovalStatement({ item }: { item: PermissionListItem }): React.JSX.Element {
  const scope = item.credential_scope?.trim().replace(/\.+$/, '');
  return (
    <div
      className="space-y-2 rounded-gl border border-border bg-muted/40 p-3 text-[12.5px] leading-relaxed text-foreground"
      data-testid="proxied-grant-statement"
    >
      <p>
        Approving lets {proxiedApprovalHolder(item)} call {item.integration_name} using the{' '}
        <span className="font-mono wrap-anywhere">{item.credential_slug}</span> credential
        {scope ? (
          <>
            , recorded as <span className="font-medium">{scope}</span>.
          </>
        ) : (
          <>, with everything that key can do in {item.integration_name}.</>
        )}{' '}
        Every call is audited, and you can revoke this grant here.
      </p>
      <a
        href={DOCS_LINKS.dataBrokering}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-[11.5px] font-medium text-link hover:underline"
      >
        How brokering works
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </a>
    </div>
  );
}

/**
 * What approving a user passthrough grant means: the upstream sees each signed-in
 * user and applies its own rules for them, the grant covers acting while they
 * are away, and the credential presents one upstream client (docs/48 §The core).
 */
function DelegationApprovalStatement({ item }: { item: PermissionListItem }): React.JSX.Element {
  return (
    <div
      className="space-y-2 rounded-gl border border-border bg-muted/40 p-3 text-[12.5px] leading-relaxed text-foreground"
      data-testid="delegation-grant-statement"
    >
      <p>
        Approving lets {proxiedApprovalHolder(item)} call {item.integration_name} as each signed-in
        user. {item.integration_name} sees the person using the app, matched by their email, and
        applies their own permissions; every call is audited to that person.
      </p>
      <p>
        Nobody connects an account: the{' '}
        <span className="font-mono wrap-anywhere">{item.credential_slug}</span> credential can act
        as any managed user in the {item.integration_name} enterprise. A call with no one signed in
        is refused, so background jobs cannot use this grant.
      </p>
      <a
        href={DOCS_LINKS.dataBrokering}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-[11.5px] font-medium text-link hover:underline"
      >
        How brokering works
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </a>
    </div>
  );
}

function PassthroughApprovalStatement({ item }: { item: PermissionListItem }): React.JSX.Element {
  return (
    <div
      className="space-y-2 rounded-gl border border-border bg-muted/40 p-3 text-[12.5px] leading-relaxed text-foreground"
      data-testid="passthrough-grant-statement"
    >
      <p>
        Approving lets {proxiedApprovalHolder(item)} call {item.integration_name} as each signed-in
        user. {item.integration_name} sees the person using the app and applies their own
        permissions, and every call is audited to that person.
      </p>
      <p>
        This grant is that approval, the same kind you give any other integration. It covers the app
        acting for those people while they are away, and there is no separate setting. Each person
        is not asked again: they agreed when they connected. Work with nobody in particular behind
        it still needs a separate service-identity credential.
      </p>
      <p>
        The <span className="font-mono wrap-anywhere">{item.credential_slug}</span> credential
        presents{' '}
        {item.credential_client_id ? (
          <>
            the upstream client{' '}
            <span className="font-mono wrap-anywhere">{item.credential_client_id}</span>
          </>
        ) : (
          <>no recorded upstream client yet</>
        )}
        .
      </p>
      <a
        href={DOCS_LINKS.dataBrokering}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-[11.5px] font-medium text-link hover:underline"
      >
        How brokering works
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function DecisionDialog({
  verb,
  item,
  open,
  isPending,
  onConfirm,
  onOpenChange,
}: DecisionDialogProps): React.JSX.Element {
  const isMobile = useIsMobile(768);
  const [reason, setReason] = React.useState('');

  // Reset reason whenever the dialog opens, the verb changes, or the target
  // item changes — so a note typed for one grant never carries over to another.
  React.useEffect(() => {
    if (open) setReason('');
  }, [open, verb, item]);

  const handleConfirm = (): void => {
    const trimmed = reason.trim();
    onConfirm(trimmed !== '' ? trimmed : undefined);
  };

  const handleCancel = (): void => {
    onOpenChange(false);
  };

  const injectedDisclosure = item?.delivery_mode === 'injected' && verb !== 'deny';
  const passthroughStatement = item?.auth_mode === 'user_delegated' && verb === 'approve';
  const delegationStatement =
    item?.acts_as_signed_in_user === true && !passthroughStatement && verb === 'approve';
  const proxiedStatement =
    item?.delivery_mode === 'proxied' &&
    verb === 'approve' &&
    !passthroughStatement &&
    !delegationStatement;

  if (!verb || !item) {
    // Render a closed GLDrawer so Radix can animate out gracefully.
    return (
      <GLDrawer
        variant="confirm"
        open={false}
        onOpenChange={onOpenChange}
        title=""
        srDescription="Decision dialog"
      >
        {null}
      </GLDrawer>
    );
  }

  return (
    <GLDrawer
      variant="confirm"
      open={open}
      onOpenChange={onOpenChange}
      preventDismiss={isPending}
      mobileBreakpoint={768}
      title={titleForVerb(verb)}
      description={`${item.integration_name} · ${item.credential_slug.toUpperCase()} — requested by ${item.requested_by}`}
      footer={
        <>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
            className={isMobile ? 'h-11' : undefined}
          >
            Cancel
          </Button>
          <Button
            variant={confirmVariantForVerb(verb)}
            onClick={handleConfirm}
            disabled={isPending}
            className={isMobile ? 'h-11' : undefined}
          >
            {confirmLabelForVerb(verb, isPending)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 py-2">
        {passthroughStatement && <PassthroughApprovalStatement item={item} />}
        {delegationStatement && <DelegationApprovalStatement item={item} />}
        {proxiedStatement && <ProxiedApprovalStatement item={item} />}
        {injectedDisclosure && (
          <div
            className="space-y-3 rounded-gl border border-border bg-muted/40 p-3"
            data-testid="injected-grant-disclosure"
          >
            <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                {verb === 'approve' ? (
                  <>
                    Approving discloses the <span className="font-medium">raw value</span> of this
                    credential, including for local development. It cannot be time-bounded or
                    remotely revoked.
                  </>
                ) : (
                  <>
                    Revoking stops future delivery, but does not remove raw values already
                    delivered. Rotate the secret upstream to invalidate existing copies.
                  </>
                )}
              </span>
            </p>
            {verb === 'revoke' && (
              <div>
                <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Pull history
                </div>
                <p
                  className="text-[11.5px] leading-snug text-muted-foreground"
                  data-testid={`pull-history-${item.credential_slug}`}
                >
                  No raw pulls recorded yet. Each raw delivery to a laptop is recorded here.
                </p>
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col gap-2">
          <Label htmlFor="decision-reason" className="text-sm font-medium text-foreground">
            Reason (optional)
          </Label>
          <Textarea
            id="decision-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Add an optional note for the audit log…"
            className="resize-none"
            rows={3}
            disabled={isPending}
          />
        </div>
      </div>
    </GLDrawer>
  );
}
